import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Archive,
  ArchiveRestore,
  Calendar,
  Download,
  GraduationCap,
  LayoutGrid,
  List,
  Mail,
  MoreVertical,
  Pencil,
  Phone,
  Plus,
  Star,
  Trash2,
  Upload,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DangerousConfirmDialog } from '@/components/ui/dangerous-confirm-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PageHeader } from '@/components/ui/page-header';
import { BulkActionBar, BulkButton } from '@/components/data-table/BulkActionBar';
import { ImportDialog, type ImportField } from '@/components/import/ImportDialog';
import {
  ModernCard,
  ModernCardBody,
  ModernCardFooter,
  ModernCardHeader,
} from '@/components/ui/modern-card';
import { EmptyStateIllustration } from '@/components/ui/empty-state-illustration';
import { exportToCSV, useBulkFormateurs, useFormateurs } from '@/hooks/queries/useFormation';
import { useAllSousSegments } from '@/hooks/queries/usePerimetres';
import { useRole } from '@/hooks/useRole';
import { formatDA } from '@/lib/format';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { buildTemplate, TEMPLATE_FORMATEURS } from '@/lib/import-templates';
import type { FormateurRow, FormateurStatut } from '@/lib/database.types';
import { FormateurFormDialog } from './FormateurFormDialog';

const VIEW_KEY = 'kost-erp-formateurs-view';
type ViewMode = 'liste' | 'carte';

export function FormateursPage(): JSX.Element {
  const { isAdmin } = useRole();
  const { data: formateurs = [], isLoading } = useFormateurs();
  const { data: sousSegments = [] } = useAllSousSegments();
  const ssMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of sousSegments) m.set(s.id, s.nom);
    return m;
  }, [sousSegments]);

  const [statutFilter, setStatutFilter] = useState<FormateurStatut | 'all'>('all');
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState('');

  const [view, setView] = useState<ViewMode>(() => {
    if (typeof localStorage === 'undefined') return 'carte';
    const saved = localStorage.getItem(VIEW_KEY);
    return saved === 'liste' ? 'liste' : 'carte';
  });
  useEffect(() => {
    localStorage.setItem(VIEW_KEY, view);
  }, [view]);

  const rows = useMemo(() => {
    let list = formateurs;
    if (!showArchived) list = list.filter((f) => f.is_active);
    if (statutFilter !== 'all') list = list.filter((f) => f.statut === statutFilter);
    if (search.length >= 2) {
      const s = search.toLowerCase();
      list = list.filter(
        (f) =>
          f.nom.toLowerCase().includes(s) ||
          f.prenom.toLowerCase().includes(s) ||
          (f.email ?? '').toLowerCase().includes(s),
      );
    }
    return list;
  }, [formateurs, statutFilter, showArchived, search]);

  const bulk = useBulkFormateurs();
  const [editing, setEditing] = useState<FormateurRow | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const ssCodeMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of sousSegments) {
      m.set(s.code.toLowerCase(), s.id);
      m.set(s.nom.toLowerCase(), s.id);
    }
    return m;
  }, [sousSegments]);

  const importFields: ImportField[] = [
    { key: 'nom_complet', label: 'Nom complet', required: true },
    { key: 'email', label: 'Email', required: true },
    { key: 'telephone', label: 'Téléphone' },
    { key: 'specialites', label: 'Spécialités' },
    { key: 'tarif_horaire', label: 'Tarif horaire DZD' },
    { key: 'tarif_journee', label: 'Tarif journée DZD' },
    { key: 'statut', label: 'Statut', required: true },
    { key: 'bio', label: 'Bio' },
    { key: 'rib', label: 'Coordonnées (RIB/IBAN)' },
    { key: 'note_interne', label: 'Remarques internes' },
  ];

  const handleImport = async (
    rows: Record<string, unknown>[],
  ): Promise<{ success: number; errors: string[] }> => {
    let success = 0;
    const errors: string[] = [];
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (!r) continue;
      try {
        const nomComplet = String(r.nom_complet ?? '').trim();
        if (!nomComplet) {
          errors.push(`L${i + 2}: nom complet manquant`);
          continue;
        }
        const tokens = nomComplet.split(/\s+/);
        const nom = tokens.length > 1 ? (tokens[tokens.length - 1] ?? '') : nomComplet;
        const prenom = tokens.length > 1 ? tokens.slice(0, -1).join(' ') : '';
        const email = String(r.email ?? '')
          .trim()
          .toLowerCase();
        if (!email) {
          errors.push(`L${i + 2}: email manquant`);
          continue;
        }
        const statutRaw = String(r.statut ?? 'externe')
          .trim()
          .toLowerCase();
        const statut: FormateurStatut = (
          ['interne', 'externe', 'vacataire'].includes(statutRaw) ? statutRaw : 'externe'
        ) as FormateurStatut;
        const specs = String(r.specialites ?? '')
          .split(/[,;]/)
          .map((s) => ssCodeMap.get(s.trim().toLowerCase()))
          .filter((v): v is string => Boolean(v));
        const payload: Partial<FormateurRow> & { nom: string; prenom: string } = {
          nom,
          prenom,
          email,
          telephone: String(r.telephone ?? '').trim() || null,
          bio: String(r.bio ?? '').trim() || null,
          tarif_horaire: r.tarif_horaire ? Number(r.tarif_horaire) : null,
          tarif_journee: r.tarif_journee ? Number(r.tarif_journee) : null,
          statut,
          specialites_sous_segments: specs,
        };
        const { error } = await supabase.from('formateurs').insert(payload as never);
        if (error) errors.push(`L${i + 2}: ${error.message}`);
        else success++;
      } catch (e) {
        errors.push(`L${i + 2}: ${(e as Error).message}`);
      }
    }
    return { success, errors };
  };

  // Bulk
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkConfirmDelete, setBulkConfirmDelete] = useState(false);
  const [bulkStatut, setBulkStatut] = useState<FormateurStatut | ''>('');
  const clearSelection = (): void => setSelected(new Set());
  const toggleSelect = (id: string): void => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAll = (): void => setSelected(new Set(rows.map((r) => r.id)));
  const selectedRows = rows.filter((r) => selected.has(r.id));

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title="Formateurs"
        subtitle={`${rows.length} formateur(s)${showArchived ? ' (archivés inclus)' : ''}`}
        actions={
          <div className="flex items-center gap-2">
            <input
              type="search"
              placeholder="Rechercher…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 w-48 rounded-md border border-input bg-background px-2 text-sm"
            />
            <select
              value={statutFilter}
              onChange={(e) => setStatutFilter(e.target.value as FormateurStatut | 'all')}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="all">— Tous statuts —</option>
              <option value="interne">Interne</option>
              <option value="externe">Externe</option>
              <option value="vacataire">Vacataire</option>
            </select>
            <label className="flex items-center gap-1 text-xs">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Archivés
            </label>
            <div className="flex overflow-hidden rounded-md border border-input">
              <button
                type="button"
                onClick={() => setView('liste')}
                className={cn(
                  'flex h-9 items-center gap-1 px-2 text-xs',
                  view === 'liste' ? 'bg-primary text-primary-foreground' : 'bg-background',
                )}
                title="Vue liste"
              >
                <List className="h-3.5 w-3.5" /> Liste
              </button>
              <button
                type="button"
                onClick={() => setView('carte')}
                className={cn(
                  'flex h-9 items-center gap-1 border-l border-input px-2 text-xs',
                  view === 'carte' ? 'bg-primary text-primary-foreground' : 'bg-background',
                )}
                title="Vue cartes"
              >
                <LayoutGrid className="h-3.5 w-3.5" /> Cartes
              </button>
            </div>
            <Button variant="outline" onClick={() => setImportOpen(true)}>
              <Upload className="h-4 w-4" /> Importer XLSX/CSV
            </Button>
            <Button
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4" /> Nouveau formateur
            </Button>
          </div>
        }
      />

      <BulkActionBar count={selected.size} onClear={clearSelection}>
        <BulkButton onClick={selectAll}>Tout ({rows.length})</BulkButton>
        <BulkButton
          onClick={async () => {
            const r = await bulk.mutateAsync({ ids: [...selected], action: 'archive' });
            toast.success(`${r.success} formateur(s) archivé(s)`);
            clearSelection();
          }}
        >
          <Archive className="h-3.5 w-3.5" /> Archiver
        </BulkButton>
        <BulkButton
          onClick={async () => {
            const r = await bulk.mutateAsync({ ids: [...selected], action: 'unarchive' });
            toast.success(`${r.success} formateur(s) réactivé(s)`);
            clearSelection();
          }}
        >
          <ArchiveRestore className="h-3.5 w-3.5" /> Réactiver
        </BulkButton>
        <div className="flex items-center gap-1">
          <select
            value={bulkStatut}
            onChange={(e) => setBulkStatut(e.target.value as FormateurStatut | '')}
            className="h-7 rounded border border-input bg-background px-2 text-xs"
          >
            <option value="">— Statut —</option>
            <option value="interne">Interne</option>
            <option value="externe">Externe</option>
            <option value="vacataire">Vacataire</option>
          </select>
          <BulkButton
            disabled={!bulkStatut}
            onClick={async () => {
              if (!bulkStatut) return;
              const r = await bulk.mutateAsync({
                ids: [...selected],
                action: 'change_statut',
                statut: bulkStatut,
              });
              toast.success(`${r.success} formateur(s) mis à jour`);
              clearSelection();
              setBulkStatut('');
            }}
          >
            Appliquer
          </BulkButton>
        </div>
        <BulkButton
          onClick={() => {
            exportToCSV(
              selectedRows.map((f) => ({
                nom: f.nom,
                prenom: f.prenom,
                email: f.email ?? '',
                telephone: f.telephone ?? '',
                statut: f.statut,
                tarif_journee: f.tarif_journee,
                tarif_horaire: f.tarif_horaire,
                note_moyenne: f.note_moyenne ?? '',
                sessions_animees: f.nombre_sessions_animees,
                specialites: (f.specialites_sous_segments ?? [])
                  .map((id) => ssMap.get(id) ?? id)
                  .join(' | '),
                actif: f.is_active ? 'oui' : 'non',
              })),
              `formateurs-${selectedRows.length}.csv`,
            );
            toast.success(`${selectedRows.length} formateur(s) exporté(s)`);
          }}
        >
          <Download className="h-3.5 w-3.5" /> Export
        </BulkButton>
        {isAdmin && (
          <BulkButton variant="destructive" onClick={() => setBulkConfirmDelete(true)}>
            <Trash2 className="h-3.5 w-3.5" /> Supprimer
          </BulkButton>
        )}
      </BulkActionBar>

      {isLoading && <div className="text-sm text-muted-foreground">Chargement…</div>}

      {view === 'carte' ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {rows.map((f) => (
            <ModernCard
              key={f.id}
              variant="formateur"
              selected={selected.has(f.id)}
              className={cn(!f.is_active && 'opacity-60')}
            >
              <input
                type="checkbox"
                checked={selected.has(f.id)}
                onChange={() => toggleSelect(f.id)}
                className="absolute left-3 top-3 z-10"
              />
              <div className="absolute right-2 top-2 z-10">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7">
                      <MoreVertical className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onSelect={() => {
                        setEditing(f);
                        setDialogOpen(true);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" /> Modifier
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <ModernCardHeader
                icon={<GraduationCap className="h-4 w-4" />}
                title={`${f.prenom} ${f.nom}`}
                subtitle={
                  <span className="flex items-center gap-1.5">
                    <Badge variant="outline" className="text-[9px]">
                      {f.statut}
                    </Badge>
                    {!f.is_active && (
                      <Badge variant="secondary" className="text-[9px]">
                        Archivé
                      </Badge>
                    )}
                  </span>
                }
              />
              <ModernCardBody>
                <div className="space-y-1 text-muted-foreground">
                  {f.email && (
                    <div className="flex items-center gap-1.5 truncate">
                      <Mail className="h-3 w-3" />
                      {f.email}
                    </div>
                  )}
                  {f.telephone && (
                    <div className="flex items-center gap-1.5">
                      <Phone className="h-3 w-3" />
                      {f.telephone}
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-3 w-3" />
                    {f.nombre_sessions_animees} session(s) animée(s)
                  </div>
                </div>
                {f.specialites_sous_segments && f.specialites_sous_segments.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {f.specialites_sous_segments.slice(0, 3).map((sid) => (
                      <Badge key={sid} variant="outline" className="text-[10px]">
                        {ssMap.get(sid) ?? sid}
                      </Badge>
                    ))}
                    {f.specialites_sous_segments.length > 3 && (
                      <Badge variant="outline" className="text-[10px]">
                        +{f.specialites_sous_segments.length - 3}
                      </Badge>
                    )}
                  </div>
                )}
              </ModernCardBody>
              <ModernCardFooter>
                {f.tarif_journee != null ? (
                  <span className="num font-semibold">{formatDA(Number(f.tarif_journee))} / j</span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
                {f.note_moyenne != null && (
                  <span className="inline-flex items-center gap-1 text-amber-600">
                    <Star className="h-3 w-3 fill-current" />
                    {Number(f.note_moyenne).toFixed(2)} / 5
                  </span>
                )}
              </ModernCardFooter>
            </ModernCard>
          ))}
          {rows.length === 0 && !isLoading && (
            <div className="col-span-full">
              <EmptyStateIllustration
                type="formateurs"
                title="Aucun formateur"
                description="Ajoutez vos premiers formateurs ou importez votre liste depuis un fichier XLSX."
              />
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-card">
          <table className="w-full text-xs">
            <thead className="border-b border-border bg-muted/40 text-[10px] uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={selected.size === rows.length && rows.length > 0}
                    onChange={() =>
                      selected.size === rows.length ? clearSelection() : selectAll()
                    }
                  />
                </th>
                <th className="px-3 py-2 text-left">Nom</th>
                <th className="px-3 py-2 text-left">Statut</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Spécialités</th>
                <th className="px-3 py-2 text-right">Tarif/j</th>
                <th className="px-3 py-2 text-right">Sessions</th>
                <th className="px-3 py-2 text-right">Note</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((f) => (
                <tr
                  key={f.id}
                  className={cn(
                    'border-b border-border/40',
                    selected.has(f.id) && 'bg-primary/5',
                    !f.is_active && 'opacity-60',
                  )}
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(f.id)}
                      onChange={() => toggleSelect(f.id)}
                    />
                  </td>
                  <td className="px-3 py-2 font-medium">
                    {f.prenom} {f.nom}
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant="outline">{f.statut}</Badge>
                    {!f.is_active && (
                      <Badge variant="secondary" className="ml-1">
                        Archivé
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{f.email ?? '—'}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(f.specialites_sous_segments ?? []).slice(0, 2).map((sid) => (
                        <Badge key={sid} variant="outline" className="text-[10px]">
                          {ssMap.get(sid) ?? sid}
                        </Badge>
                      ))}
                      {(f.specialites_sous_segments ?? []).length > 2 && (
                        <Badge variant="outline" className="text-[10px]">
                          +{(f.specialites_sous_segments ?? []).length - 2}
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="num px-3 py-2 text-right">
                    {f.tarif_journee != null ? formatDA(Number(f.tarif_journee)) : '—'}
                  </td>
                  <td className="num px-3 py-2 text-right">{f.nombre_sessions_animees}</td>
                  <td className="num px-3 py-2 text-right">
                    {f.note_moyenne != null ? Number(f.note_moyenne).toFixed(2) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7">
                          <MoreVertical className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onSelect={() => {
                            setEditing(f);
                            setDialogOpen(true);
                          }}
                        >
                          <Pencil className="h-3.5 w-3.5" /> Modifier
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={9} className="py-4 text-center text-muted-foreground">
                    Aucun formateur.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <FormateurFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        onSaved={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
      />

      <ImportDialog<Record<string, unknown>>
        open={importOpen}
        onOpenChange={setImportOpen}
        title="Importer des formateurs (XLSX/CSV)"
        fields={importFields}
        onImport={handleImport}
        generateTemplate={() => buildTemplate(TEMPLATE_FORMATEURS)}
        templateFilename={TEMPLATE_FORMATEURS.filename}
      />

      <DangerousConfirmDialog
        open={bulkConfirmDelete}
        onOpenChange={setBulkConfirmDelete}
        title={`Supprimer ${selected.size} formateur${selected.size > 1 ? 's' : ''} ?`}
        description="Action irréversible. Préférez l'archivage si le formateur a des sessions historiques."
        consequences={[
          `${selected.size} formateur(s) effacés`,
          'Les sessions historiques seront orphelines (formateur_principal_id = NULL)',
          'Audit log conservé',
        ]}
        challenge="SUPPRIMER"
        loading={bulk.isPending}
        onConfirm={async () => {
          const r = await bulk.mutateAsync({ ids: [...selected], action: 'delete' });
          if (r.errors.length === 0) toast.success(`${r.success} formateur(s) supprimé(s)`);
          else
            toast.warning(`${r.success} OK · ${r.errors.length} échec(s) — ${r.errors[0] ?? ''}`);
          setBulkConfirmDelete(false);
          clearSelection();
        }}
      />
    </div>
  );
}
